import React, { useState, useEffect } from "react";
import PricingToggle from "./misc/PricingToggle";
import PricingPlanPay from "./misc/PricingPlanPay";
import { useSearchParams, useNavigate } from "react-router-dom";
import BankTransferModal from "../../payment/misc/BankTransferModal";
import { featuresService } from "../../../services/api/settings";
import { Feature } from "../../../store/types/settings";
import AddOnsAi from "./misc/AddOnsAi";
import SelectedFeaturesFloating, {
  SelectedItem,
} from "../misc/SelectedFeaturesFloating";
// import LifecyclePhasesSection from "../misc/LifecyclePhasesSection";
import { offeringsService } from "../../../services/api/settings";
import { OfferingPlan, OfferingAddon } from "../../../store/types/settings";
import {
  Zap,
  Star,
  Crown,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  CheckCircle2,
  ArrowLeft,
} from "lucide-react";
import Navbar from "../../../components/Navbar2";

const MakePayment: React.FC = () => {
  const [billingType, setBillingType] = useState<
    "Monthly" | "Quarterly" | "Yearly"
  >(() => {
    const freq = new URLSearchParams(window.location.search).get("frequency");
    if (freq === "YEARLY") return "Yearly";
    if (freq === "QUARTERLY") return "Quarterly";
    return "Monthly";
  });
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);
  const [selectedDirectAddOnIds, setSelectedDirectAddOnIds] = useState<
    string[]
  >([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<Feature[]>([]);
  const [isBankTransferModalOpen, setIsBankTransferModalOpen] = useState(false);
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = searchParams.get("Id");
  const tier = searchParams.get("tier");
  const provider = searchParams.get("provider");
  const directPlanTier = searchParams.get("directPlan");
  const includeSetupFee = searchParams.get("includeSetupFee") === "true";
  const [offeringPlans, setOfferingPlans] = useState<OfferingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [offeringAddons, setOfferingAddons] = useState<OfferingAddon[]>([]);

  const [lifecyclePhases, setLifecyclePhases] = useState<any[]>([]);
  const [selectedLifecyclePhaseIds, setSelectedLifecyclePhaseIds] = useState<
    number[]
  >([]);

  const getTierIcon = (tier: string | null) => {
    switch (tier?.toUpperCase()) {
      case "STARTER":
        return Zap;
      case "GROWTH":
        return Star;
      case "SCALE":
        return Crown;
      default:
        return Zap;
    }
  };

  const plans = offeringPlans.map((plan) => ({
    name: plan.title,
    tier: plan.tier || "",
    badge: plan.subtitle,
    priceMonthly: parseFloat(plan.monthlyPrice).toLocaleString(),
    priceQuarterly: plan.quarterlyPrice
      ? parseFloat(String(plan.quarterlyPrice)).toLocaleString()
      : (parseFloat(plan.monthlyPrice) * 3).toLocaleString(),
    priceYearly: parseFloat(plan.yearlyPrice).toLocaleString(),
    features: plan.points,
    icon: getTierIcon(plan.tier),
    popular: plan.tier === "GROWTH",
    isVip: plan.tier === "ENTERPRISE",
    integrationServices:
      (plan.costBreakdown?.deploymentCosts?.length ?? 0) > 0
        ? plan
            .costBreakdown!.deploymentCosts.filter((d: any) => d && d.name)
            .map((d: any) => ({
              name: d.name,
              cost: d.price.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }),
            }))
        : plan.setupFee || plan.integrationCost
          ? [
              {
                name: "Standard Onboarding & Training",
                cost: (
                  parseFloat(plan.setupFee || plan.integrationCost || "0") || 0
                ).toLocaleString(),
              },
            ]
          : [],
  }));

  useEffect(() => {
    if (directPlanTier) {
      // Validate tier before navigation
      if (!tier) {
        console.error("Tier is required for order form navigation");
        return;
      }

      const freq = searchParams.get("frequency") || "MONTHLY";
      const fIds = searchParams.get("featureIds");
      const fUrl = fIds ? `&featureIds=${fIds}` : "";

      const daIds = searchParams.get("directAddOnIds");
      const daUrl = daIds ? `&directAddOnIds=${daIds}` : "";

      navigate(
        `/order-form?Id=${id}&tier=${tier}&frequency=${freq}${fUrl}${daUrl}&directPlan=${directPlanTier}&includeSetupFee=${includeSetupFee}`,
      );
    }
  }, [directPlanTier, id, tier, includeSetupFee, searchParams, navigate]);

  const directPlan = directPlanTier
    ? {
        tier: directPlanTier as "STARTER" | "GROWTH" | "SCALE",
        includeSetupFee,
      }
    : undefined;

  // Fetch offerings and features
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const offeringsResponse = await offeringsService.getAll();
        if (offeringsResponse.status === "success" && offeringsResponse.data) {
          setOfferingPlans(offeringsResponse.data.RECRUITER.plans);
          setOfferingAddons([
            ...offeringsResponse.data.RECRUITER.addons,
            ...offeringsResponse.data.DIRECT.addons,
          ]);
          if (offeringsResponse.data.LIFECYCLE?.phases) {
            setLifecyclePhases(offeringsResponse.data.LIFECYCLE.phases);
          }
        }

        if (id) {
          const featuresResponse = await featuresService.getAllAvailable(id);
          if (featuresResponse.status === "success" && featuresResponse.data) {
            setFeatures(featuresResponse.data);
          }
        }
      } catch (error) {
        console.error("Error fetching payment data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Read featureIds from URL parameters and set selectedFeatureIds
  useEffect(() => {
    const featureIdsParam = searchParams.get("featureIds");
    if (featureIdsParam) {
      const featureIdsArray = featureIdsParam
        .split(",")
        .filter((id) => id.trim() !== "");
      setSelectedFeatureIds(featureIdsArray);
    }
  }, [searchParams]);

  // Read directAddOnIds from URL parameters
  useEffect(() => {
    const directAddOnIdsParam =
      searchParams.get("directAddOnIds") || searchParams.get("directAddOns");
    if (directAddOnIdsParam) {
      const idsArray = directAddOnIdsParam
        .split(",")
        .filter((id) => id.trim() !== "");
      setSelectedDirectAddOnIds(idsArray);
    }
  }, [searchParams]);

  // Read lifecyclePhaseIds from URL parameters
  useEffect(() => {
    const lifecyclePhaseIdsParam = searchParams.get("lifecyclePhaseIds");
    if (lifecyclePhaseIdsParam) {
      const idsArray = lifecyclePhaseIdsParam
        .split(",")
        .map(Number)
        .filter((id) => !isNaN(id));
      setSelectedLifecyclePhaseIds(idsArray);
    }
  }, [searchParams]);

  // Calculate selectedFeatures when features or selectedFeatureIds change
  useEffect(() => {
    if (features.length > 0 && selectedFeatureIds.length > 0) {
      const filtered = features.filter((f) =>
        selectedFeatureIds.includes(f.id),
      );
      setSelectedFeatures(filtered);
    } else {
      setSelectedFeatures([]);
    }
  }, [features, selectedFeatureIds]);

  const handleFeatureSelectionChange = (featureIds: string[]) => {
    setSelectedFeatureIds(featureIds);
  };

  const handleLifecyclePhaseToggle = (phaseId: number) => {
    setSelectedLifecyclePhaseIds((prev) => {
      const isSelected = prev.includes(phaseId);
      let newIds = isSelected
        ? prev.filter((id) => id !== phaseId)
        : [...prev, phaseId];

      const mvpPhase = lifecyclePhases.find(
        (p) => p.name && p.name.trim().toLowerCase() === "mvp foundation",
      );
      if (mvpPhase) {
        const mvpId = mvpPhase.id;
        const hasOthersSelected = newIds.some((id) => id !== mvpId);

        if (hasOthersSelected) {
          if (!newIds.includes(mvpId)) {
            newIds.push(mvpId);
          }
        } else {
          if (phaseId === mvpId && isSelected) {
            newIds = [];
          }
        }
      }

      setSearchParams((p) => {
        if (newIds.length > 0) {
          p.set("lifecyclePhaseIds", newIds.join(","));
        } else {
          p.delete("lifecyclePhaseIds");
        }
        return p;
      });

      return newIds;
    });
  };

  const handleDirectAddOnToggle = (id: string) => {
    setSelectedDirectAddOnIds((prev) => {
      const newIds = prev.includes(id)
        ? prev.filter((i) => i !== id)
        : [...prev, id];

      // Update URL params for sync
      setSearchParams((p) => {
        if (newIds.length > 0) {
          p.set("directAddOnIds", newIds.join(","));
        } else {
          p.delete("directAddOnIds");
        }
        return p;
      });
      return newIds;
    });
  };

  const handleDirectAddOnRemove = (id: string) => {
    setSelectedDirectAddOnIds((prev) => {
      const newIds = prev.filter((i) => i !== id);
      // Update URL params for sync
      setSearchParams((p) => {
        if (newIds.length > 0) {
          p.set("directAddOnIds", newIds.join(","));
        } else {
          p.delete("directAddOnIds");
        }
        return p;
      });
      return newIds;
    });
  };

  const handleFeatureRemove = (featureId: string) => {
    setSelectedFeatureIds((prev) => {
      const newIds = prev.filter((id) => id !== featureId);
      // Update URL params for sync
      setSearchParams((p) => {
        if (newIds.length > 0) {
          p.set("featureIds", newIds.join(","));
        } else {
          p.delete("featureIds");
        }
        return p;
      });
      return newIds;
    });

    const numId = Number(featureId);
    if (!isNaN(numId) && selectedLifecyclePhaseIds.includes(numId)) {
      handleLifecyclePhaseToggle(numId);
    }
  };

  const currentPlan = plans.find((p) => p.tier === tier);
  const planFeatures = currentPlan
    ? currentPlan.features.map((f) => ({
        id: f,
        displayName: f,
        price: 0,
      }))
    : [];

  const selectedAiAddOns = offeringAddons.filter((addon) =>
    selectedDirectAddOnIds.includes(addon.id),
  );

  const selectedDirectAddOns = offeringAddons.filter((addon) =>
    selectedDirectAddOnIds.includes(addon.id),
  );

  const allSelectedItems: SelectedItem[] = [
    // ...planFeatures,
    ...selectedFeatures.map((f) => ({
      id: f.id,
      displayName: f.displayName,
      price: f.price,
    })),
    ...selectedAiAddOns.filter(Boolean).map((a) => ({
      id: a.id,
      displayName: a.name || "Unknown Add-on",
      price: typeof a.price === "string" ? parseFloat(a.price) : a.price || 0,
    })),
    ...selectedDirectAddOns.filter(Boolean).map((a: OfferingAddon) => ({
      id: a.id,
      displayName: a.name || "Unknown Add-on",
      price: typeof a.price === "string" ? parseFloat(a.price) : a.price || 0,
    })),
    ...lifecyclePhases
      .filter((p) => selectedLifecyclePhaseIds.includes(p.id) || p.isMandatory)
      .map((p) => ({
        id: p.id.toString(),
        name: p.name,
        displayName: p.name,
        description: p.description,
        price:
          p.setupFee +
          p.monthlySubscription *
            (billingType === "Yearly"
              ? 12
              : billingType === "Quarterly"
                ? 3
                : 1),
        currency: "NGN",
        category: "LIFECYCLE" as const,
      })),
  ];

  const calculateFloatingTotal = () => {
    const total = allSelectedItems.reduce(
      (sum, item) => sum + (item.price || 0),
      0,
    );
    return total.toLocaleString();
  };

  const handleBankTransferClick = () => {
    setIsBankTransferModalOpen(true);
  };

  const frequency: "MONTHLY" | "QUARTERLY" | "YEARLY" =
    billingType === "Yearly"
      ? "YEARLY"
      : billingType === "Quarterly"
        ? "QUARTERLY"
        : "MONTHLY";

  console.log("Selected Tier:", tier);
  console.log("Selected Features:", selectedFeatures);
  console.log("Selected Add-ons:", selectedDirectAddOnIds);

  return (
    <div className="min-h-screen bg-gray-50 font-abel pb-20">
      <Navbar title="Login" link="/login" />
      <div className="max-w-7xl mx-auto px-4 pt-10">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div
              onClick={() => history.back()}
              className="text-blue-600 hover:text-blue-800 cursor-pointer"
            >
              <ArrowLeft className="w-6 h-6" />
            </div>
            <h1 className="text-3xl font-medium text-gray-900">
              Review Selection
            </h1>
          </div>
          <button
            onClick={() => setShowAllPlans(!showAllPlans)}
            className="px-6 py-2 bg-white border border-blue-600 text-blue-600 rounded-full hover:bg-blue-50 transition-colors font-medium flex items-center gap-2"
          >
            <Zap className="w-4 h-4" />
            {showAllPlans ? "Hide other plans" : "Compare all plans"}
          </button>
        </div>

        <PricingToggle
          billingType={billingType}
          setBillingType={(type) => {
            setBillingType(type);
            setSearchParams((p) => {
              p.set(
                "frequency",
                type === "Yearly"
                  ? "YEARLY"
                  : type === "Quarterly"
                    ? "QUARTERLY"
                    : "MONTHLY",
              );
              return p;
            });
          }}
        />

        {showAllPlans && (
          <div className="mt-10 mb-16 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.map((plan, index) => {
                const isSelected = plan.tier === tier;
                const isExpanded = expandedPlan === index;
                const Icon = plan.icon;
                return (
                  <div
                    key={index}
                    className={`rounded-[20px] shadow-lg overflow-hidden flex flex-col bg-white border-2 transition-all duration-300 ${
                      isSelected
                        ? "border-blue-600 ring-4 ring-blue-50 scale-[1.02]"
                        : "border-transparent hover:border-gray-200"
                    }`}
                  >
                    <div className="p-8 flex flex-col h-full">
                      <div className="flex justify-between items-start mb-1">
                        <div className="flex items-center gap-3">
                          {/* <div
                            className={`p-2 rounded-lg ${plan.isVip ? "bg-amber-100" : "bg-blue-100"}`}
                          >
                            <Icon
                              className={`w-6 h-6 ${plan.isVip ? "text-amber-600" : "text-blue-600"}`}
                            />
                          </div> */}
                          <h3 className="text-2xl font-medium text-gray-900">
                            {plan.name}
                          </h3>
                        </div>
                        {isSelected && (
                          <span className="bg-blue-600 text-white text-[10px] uppercase px-2 py-0.5 rounded-full font-bold">
                            Selected
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-blue-600 font-medium mb-4">
                        {plan.badge}
                      </p>

                      <div className="mb-6">
                        <div className="flex items-baseline">
                          <span className="text-3xl font-normal text-gray-900">
                            <span className="text-lg align-super">N</span>
                            {billingType === "Yearly"
                              ? plan.priceYearly
                              : billingType === "Quarterly"
                                ? plan.priceQuarterly
                                : plan.priceMonthly}
                          </span>
                          <span className="text-gray-500 ml-1 text-sm">
                            /
                            {billingType === "Yearly"
                              ? "year"
                              : billingType === "Quarterly"
                                ? "quarter"
                                : "month"}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3 mb-6 flex-grow">
                        {plan.features
                          .slice(0, isExpanded ? undefined : 4)
                          .map((f, i) => (
                            <div key={i} className="flex items-start gap-2.5">
                              <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700 text-[13px]">
                                {f}
                              </span>
                            </div>
                          ))}
                      </div>

                      {plan.features.length > 4 && (
                        <button
                          onClick={() =>
                            setExpandedPlan(isExpanded ? null : index)
                          }
                          className="text-blue-600 text-xs font-semibold flex items-center gap-1 mb-6 hover:underline"
                        >
                          {isExpanded ? (
                            <>
                              Show less <ChevronUp className="w-3 h-3" />
                            </>
                          ) : (
                            <>
                              Show all features{" "}
                              <ChevronDown className="w-3 h-3" />
                            </>
                          )}
                        </button>
                      )}

                      {(plan as any).integrationServices?.some(
                        (s: any) => s.name,
                      ) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedPlan(isExpanded ? null : index);
                          }}
                          className={`w-full py-2.5 px-3 mb-6 border rounded-lg font-m hover:transition-all flex items-center justify-center gap-2 border-gray-100 text-gray-500 hover:bg-gray-50`}
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUp className="w-4 h-4" />
                              <span className="text-xs">
                                Hide Integration Plan
                              </span>
                            </>
                          ) : (
                            <>
                              <ChevronUp className="w-4 h-4 rotate-180" />
                              <span className="text-xs">
                                View Integration Plan
                              </span>
                            </>
                          )}
                        </button>
                      )}

                      {isExpanded &&
                        (plan as any).integrationServices?.some(
                          (s: any) => s.name,
                        ) && (
                          <div
                            className={`mb-6 pb-6 border-t pt-6 border-gray-100`}
                          >
                            <div className="flex justify-between items-center mb-4">
                              <h4 className={`text-sm font-bold text-gray-900`}>
                                Deployment & Setup Plan
                              </h4>
                              <span
                                className={`text-[10px] font-bold text-gray-400 uppercase tracking-wider`}
                              >
                                NGN
                              </span>
                            </div>
                            <ul className="space-y-3">
                              {(plan as any).integrationServices
                                .filter((s: any) => s.name)
                                .map((service: any, i: number) => (
                                  <li
                                    key={i}
                                    className="flex items-start justify-between text-xs"
                                  >
                                    <span className="text-gray-500 leading-relaxed pr-4">
                                      {service.name}
                                    </span>
                                    <span className="font-bold text-gray-900 whitespace-nowrap">
                                      {service.cost}
                                    </span>
                                  </li>
                                ))}
                            </ul>
                          </div>
                        )}

                      {!isSelected ? (
                        <button
                          onClick={() => {
                            setSearchParams((p) => {
                              p.set("tier", plan.tier);
                              return p;
                            });
                          }}
                          className="w-full py-3 px-4 rounded-xl border-2 border-blue-600 text-blue-600 font-bold hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center gap-2"
                        >
                          Switch to this plan
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      ) : (
                        <div className="w-full py-3 px-4 rounded-xl bg-blue-50 text-blue-600 font-bold text-center border border-blue-200">
                          Current Selection
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-3 mt-10">
          <div className="lg:col-span-1">
            <PricingPlanPay
              billingType={billingType}
              organizationId={id ?? ""}
              selectedTier={tier ?? undefined}
              selectedFeatureIds={selectedFeatureIds}
              selectedFeatures={selectedFeatures}
              onFeatureRemove={handleFeatureRemove}
              provider={provider || undefined}
              onBankTransferClick={
                provider === "bank-transfer"
                  ? handleBankTransferClick
                  : undefined
              }
              directPlan={directPlan}
              selectedDirectAddOnIds={selectedDirectAddOnIds}
              onDirectAddOnRemove={handleDirectAddOnToggle}
              plans={offeringPlans}
              addons={offeringAddons}
              selectedLifecyclePhaseIds={selectedLifecyclePhaseIds}
            />
          </div>
          <div className="lg:col-span-2">
            <AddOnsAi
              organizationId={id || undefined}
              selectedIds={selectedDirectAddOnIds}
              onToggle={handleDirectAddOnToggle}
            />
            {/* <LifecyclePhasesSection
              phases={lifecyclePhases}
              selectedPhaseIds={selectedLifecyclePhaseIds}
              onTogglePhase={handleLifecyclePhaseToggle}
              loading={loading}
            /> */}
          </div>
        </div>
      </div>
      {/* <AddOnServices
        onSelectionChange={handleFeatureSelectionChange}
        selectedFeatureIds={selectedFeatureIds}
        organizationId={id ?? ""}
        features={features}
        loading={false}
      /> */}
      {/* <DirectPricingSection
        billingType={billingType}
        orgId={id ?? ""}
        selectedTier={tier ?? undefined}
        selectedFeatureIds={selectedFeatureIds}
        selectedFeatures={selectedFeatures}
        onFeatureRemove={handleFeatureRemove}
        provider={provider || undefined}
        onBankTransferClick={
          provider === "bank-transfer" ? handleBankTransferClick : undefined
        }
      /> */}
      {/* <DirectAddOns /> */}

      {/* Bank Transfer Modal */}
      {provider === "bank-transfer" && (
        <BankTransferModal
          isOpen={isBankTransferModalOpen}
          onClose={() => setIsBankTransferModalOpen(false)}
          organizationId={id || ""}
          tier={tier || "STARTER"}
          frequency={frequency}
          featureIds={selectedFeatureIds}
        />
      )}

      <SelectedFeaturesFloating
        selectedItems={allSelectedItems}
        onRemove={(id) => {
          handleFeatureRemove(id);
          handleDirectAddOnRemove(id);
        }}
        totalPrice={calculateFloatingTotal()}
        billingType={billingType}
        title="Payment selection"
      />
    </div>
  );
};

export default MakePayment;
